import { ITrackType, useClickTracker, ClickstreamAnalyticsProvider } from '@shared-ui/clickstream-analytics-context';
import * as React from 'react';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */

var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
  var e = new Error(message);
  return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};
var BernieAnalyticsContext = React.createContext(null);
var BernieAnalyticsProvider = function (props) {
  var children = props.children,
    analytics = props.analytics;
  return React.createElement(BernieAnalyticsContext.Provider, {
    value: analytics
  }, children || null);
};
var useBernieAnalyticsContext = function () {
  return React.useContext(BernieAnalyticsContext);
};

/* eslint-disable @typescript-eslint/no-unused-vars */
var noop = function () {};
var mockPerformanceMetrics = {
  addData: noop,
  send: noop,
  setContext: noop,
  reset: noop,
  getPageUsableTime: noop,
  mark: function (name) {},
  markPageUsable: function (data, callback) {},
  findMark: function (name) {
    return {
      startTime: 0
    };
  }
};
var getPerformanceMetrics = function () {
  if (typeof window === 'object') {
    return require('platform-analytics-performance-metrics');
  }
  return mockPerformanceMetrics;
};

// private statics
var ERROR_PREFIX = 'Analytics Error:';
var TIMEOUT = 500;
var PLATFORM_ANALYTICS_ERROR = new SystemEvent(SystemEventLevel.ERROR, 'PLATFORM_ANALYTICS_ERROR');
var MIN_EVENT_LENGTH = 3;
function trackEvent(namespace, rfrr, linkName, uisPrimeMicroMessages, events, trackImmediate, logger, platformAnalytics) {
  if (uisPrimeMicroMessages === void 0) {
    uisPrimeMicroMessages = [];
  }
  if (trackImmediate === void 0) {
    trackImmediate = false;
  }
  if (namespace && (typeof namespace !== 'string' || namespace.length < MIN_EVENT_LENGTH)) {
    throw new Error("".concat(ERROR_PREFIX, " interaction namespace must be empty or a string with a minimum length of ").concat(MIN_EVENT_LENGTH));
  } else if (!namespace) {
    namespace = '';
  }
  if (typeof rfrr !== 'string' || rfrr.length < MIN_EVENT_LENGTH) {
    // throw error if no rfrr is passed, it means we're missing intended tracking
    throw new Error("".concat(ERROR_PREFIX, " tracked event rfrr must be a string with a minimum length of ").concat(MIN_EVENT_LENGTH));
  }
  if (typeof linkName !== 'string' || linkName.length < MIN_EVENT_LENGTH) {
    // throw error if no linkName is passed, it means we're missing intended tracking
    throw new Error("".concat(ERROR_PREFIX, " tracked event linkName must be a string with a minimum length of ").concat(MIN_EVENT_LENGTH));
  }
  if (typeof window === 'object') {
    var label = _setLabel(namespace, rfrr);
    var referrerMicroMessage = {
      schemaName: 'referrer',
      messageContent: {
        referrerId: label,
        linkName: linkName,
        eventType: 'click'
      }
    };
    if (!uisPrimeMicroMessages) {
      uisPrimeMicroMessages = [];
    }
    uisPrimeMicroMessages.push(referrerMicroMessage);
    if (events && Array.isArray(events)) {
      events.forEach(function (event) {
        var eventMicroMessage = {
          schemaName: 'events',
          messageContent: event
        };
        uisPrimeMicroMessages.push(eventMicroMessage);
      });
    }
    var fireTracking = function () {
      try {
        platformAnalytics.trackEvent(uisPrimeMicroMessages);
      } catch (err) {
        if (logger) {
          logger.logEvent(PLATFORM_ANALYTICS_ERROR, err);
        }
      }
    };
    if (trackImmediate) {
      fireTracking();
    } else {
      // free up the UI thread so it can move on to the intended click task
      // and also unblock the event propagation  if this timeout callback throws an error
      setTimeout(fireTracking, TIMEOUT);
    }
  }
}
function _setLabel(namespace, rfrr) {
  if (!namespace) {
    return rfrr;
  }
  var first = rfrr.charAt(0);
  var hasDelimiter = first === '.' || first === ':' || first === '|';
  var delimiter = hasDelimiter ? '' : '.';
  return namespace.concat(delimiter, rfrr);
}
var PlatformAnalyticsNoop = /** @class */function () {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function class_1() {
    /*noop*/
  }
  class_1.prototype.collect = function () {
    /*noop*/
  };
  class_1.prototype.publish = function () {
    /*noop*/
  };
  class_1.prototype.publishClientSidePayload = function () {
    /*noop*/
  };
  class_1.prototype.fireAmcvBeaconWithTraceId = function () {
    /*noop*/
  };
  class_1.prototype.markPageUsable = function () {
    /*noop*/
  };
  class_1.prototype.trackEvent = function () {
    /*noop*/
  };
  class_1.prototype.getPerformanceMetrics = function () {
    return getPerformanceMetrics();
  };
  class_1.prototype.getClientLog = function () {
    /*noop*/
  };
  return class_1;
}();
function createPlatformAnalytics() {
  if (typeof window === 'object') {
    var PlatformAnalytics = require('platform-analytics-prime');
    return new PlatformAnalytics({
      isPwa: true,
      isPublishWebVitals: true
    });
  }
  return new PlatformAnalyticsNoop();
}
var BernieAnalytics = /** @class */function () {
  function BernieAnalytics() {}
  BernieAnalytics.prototype.trackEvent = function (namespace, rfrr, linkName, omnitureData, events, trackImmediate, logger, uisPrimeMessages) {
    if (trackImmediate === void 0) {
      trackImmediate = false;
    }
    if (uisPrimeMessages === void 0) {
      uisPrimeMessages = [];
    }
    trackEvent(namespace, rfrr, linkName, uisPrimeMessages, events, trackImmediate, logger, this.getPlatformAnalytics());
  };
  BernieAnalytics.prototype.resetAnalyticsForNewPage = function () {
    this.platformAnalytics = createPlatformAnalytics();
  };
  BernieAnalytics.prototype.getPlatformAnalytics = function () {
    if (this.platformAnalytics === undefined) {
      this.platformAnalytics = createPlatformAnalytics();
    }
    return this.platformAnalytics;
  };
  BernieAnalytics.prototype.setPlatformAnalytics = function (platformAnalytics) {
    this.platformAnalytics = platformAnalytics;
  };
  return BernieAnalytics;
}();

/**
 * BEX (UIS-Prime) implementation of IClickstreamAnalytics interface
 */
var UisPrimeClickstream = /** @class */function () {
  function UisPrimeClickstream(analytics) {
    this.bernieAnalytics = analytics;
    this.track = this.track.bind(this);
    this.reset = this.reset.bind(this);
  }
  UisPrimeClickstream.prototype.track = function (type, options) {
    if (type === ITrackType.EVENT) {
      this.trackEvent(options.data, options.config);
    }
  };
  UisPrimeClickstream.prototype.reset = function () {
    var _a, _b;
    (_b = (_a = this.bernieAnalytics).resetAnalyticsForNewPage) === null || _b === void 0 ? void 0 : _b.call(_a);
  };
  UisPrimeClickstream.prototype.trackEvent = function (data, config) {
    var _this = this;
    var _a, _b;
    if (config && config.delay) {
      setTimeout(function () {
        var _a, _b;
        (_b = (_a = _this.bernieAnalytics).getPlatformAnalytics) === null || _b === void 0 ? void 0 : _b.call(_a).trackEvent(data);
      }, 0);
    } else {
      (_b = (_a = this.bernieAnalytics).getPlatformAnalytics) === null || _b === void 0 ? void 0 : _b.call(_a).trackEvent(data);
    }
  };
  return UisPrimeClickstream;
}();
var createUisPrimeClickstream = function (bernieAnalytics) {
  return new UisPrimeClickstream(bernieAnalytics);
};
var isServer = function () {
  return typeof window === 'undefined';
};
var egClickstreamSdkNoop = {
  publish: function () {},
  updateContext: function () {}
};
var egClickstreamSdkInstance;
var getCallbacks = function (logger) {
  return {
    onFetchError: function (error) {
      logger === null || logger === void 0 ? void 0 : logger.info('EGCS_FETCH_ERROR', "EGCS failed on fetch. ".concat(error));
    },
    onInvalidPayload: function (error) {
      logger === null || logger === void 0 ? void 0 : logger.info('EGCS_INVALID_PAYLOAD', "EGCS failed due to invalid payload. ".concat(error));
    },
    onPublishError: function (error) {
      logger === null || logger === void 0 ? void 0 : logger.info('EGCS_PUBLISH_ERROR', "EGCS failed on publish. ".concat(error));
    }
  };
};
var getNewInstance = function (options) {
  var sdk = require('eg-clickstream-sdk-js');
  var callbacks = getCallbacks(options === null || options === void 0 ? void 0 : options.logger);
  return sdk.instance && sdk.instance.getNewInstance(callbacks, options === null || options === void 0 ? void 0 : options.egClickstream);
};
/**
 * Creates and returns a new instance of EgClickstreamSdk
 */
var createEgClickstreamSdkInstance = function (options) {
  return isServer() ? egClickstreamSdkNoop : getNewInstance(options);
};
/**
 * Gets the instance of singleton EgClickstreamSdk class
 */
var getEgClickstreamSdkInstance = function (options) {
  if (!egClickstreamSdkInstance) {
    egClickstreamSdkInstance = createEgClickstreamSdkInstance(options);
  }
  return egClickstreamSdkInstance;
};

/**
 * The implementation of EgClickstreamService interface
 */
var EgClickstreamServiceImpl = /** @class */function () {
  function EgClickstreamServiceImpl(options) {
    this.egClickstreamSdkInstance = getEgClickstreamSdkInstance(options);
  }
  EgClickstreamServiceImpl.prototype.publish = function (data) {
    if (!this.egClickstreamSdkInstance) {
      return;
    }
    var clientPayload = data === null || data === void 0 ? void 0 : data.clientPayload;
    var serverPayload = data === null || data === void 0 ? void 0 : data.serverPayload;
    // Make it backward-compatible with older versions where payload is all client-provided without breaking up into client/server-provided portions
    if (!clientPayload && !serverPayload) {
      this.egClickstreamSdkInstance.publish(data);
      return;
    }
    // Fallback to an empty object if clientPayload is undefined as clientPayload is required by SDK publish API
    this.egClickstreamSdkInstance.publish(clientPayload || {}, serverPayload);
  };
  EgClickstreamServiceImpl.prototype.updateContext = function (data) {
    if (this.egClickstreamSdkInstance) {
      this.egClickstreamSdkInstance.updateContext(data);
    }
  };
  return EgClickstreamServiceImpl;
}();
var createEgClickstreamService = function (options) {
  return new EgClickstreamServiceImpl(options);
};

/**
 * EG-Clickstream implementation of IClickstreamAnalytics interface
 */
var EgClickstream = /** @class */function () {
  function EgClickstream(options) {
    this.egClickstreamService = createEgClickstreamService(options);
    this.track = this.track.bind(this);
    this.reset = this.reset.bind(this);
  }
  EgClickstream.prototype.track = function (type, options) {
    var data = options.data;
    // Do nothing if data is an array
    if (Array.isArray(data)) {
      return;
    }
    if (type === ITrackType.EVENT) {
      this.egClickstreamService.publish(data);
    }
  };
  EgClickstream.prototype.reset = function (options) {
    var data = options && options.data;
    if (data) {
      this.egClickstreamService.updateContext(data);
    }
  };
  return EgClickstream;
}();
var createEgClickstream = function (options) {
  return new EgClickstream(options);
};
var clickstreamMap = {
  'uis-prime': createUisPrimeClickstream
};
/**
 * The implementation of IClickstreamAnalytics interface
 */
var Clickstream = /** @class */function () {
  function Clickstream(options, analytics) {
    this.setBrandClickstream(options, analytics);
    this.egClickstream = createEgClickstream(options);
    this.track = this.track.bind(this);
    this.reset = this.reset.bind(this);
  }
  Clickstream.prototype.track = function (type, options) {
    this.getClickstreamObject(options).track(type, options);
  };
  Clickstream.prototype.reset = function (options) {
    this.getClickstreamObject(options).reset(options);
  };
  /**
   * Gets brand specific or EG clickstream object based on the provided options
   * Returns egClickstream if the value of options.config.isEgClickstream is true
   */
  Clickstream.prototype.getClickstreamObject = function (options) {
    var clickstreamObject = this.brandClickstream;
    if (options && options.config && options.config.isEgClickstream === true) {
      clickstreamObject = this.egClickstream;
    }
    return clickstreamObject;
  };
  /**
   * Sets brandClickstream to the clickstream object that matches the provided name
   * and uses brandClickstreamNoop if no match found
   * @param config
   */
  Clickstream.prototype.setBrandClickstream = function (options, analytics) {
    var brandClickstreamNoop = {
      track: function () {},
      reset: function () {}
    };
    var brandClickstreamFactory = clickstreamMap[options === null || options === void 0 ? void 0 : options.brandClickstream];
    this.brandClickstream = brandClickstreamFactory ? brandClickstreamFactory(analytics) : brandClickstreamNoop;
  };
  return Clickstream;
}();
var createClickstream = function (options, analytics) {
  return new Clickstream(options, analytics);
};
function getClickstreamAnalytics(options, analytics) {
  return createClickstream(options, analytics);
}
var withClickTracker = function (WrappedComponent) {
  var ClickTracker = function (props) {
    var rfrr = props.rfrr,
      linkName = props.linkName,
      trackImmediate = props.trackImmediate,
      uisPrimeMicroMessages = props.uisPrimeMicroMessages; // eslint-disable-line react/prop-types
    var newProps = __assign({}, props);
    var clickTracker = useClickTracker();
    // no need to pass these down further, if they trickle down to native DOM elements, it will throw an error
    delete newProps.rfrr;
    delete newProps.linkName;
    delete newProps.events;
    delete newProps.trackImmediate;
    delete newProps.uisPrimeMicroMessages;
    if (clickTracker && typeof rfrr === 'string' && typeof linkName === 'string') {
      newProps.onClickCapture = function () {
        clickTracker(rfrr, linkName, trackImmediate, uisPrimeMicroMessages);
      };
    }
    return React.createElement(WrappedComponent, __assign({}, newProps));
  };
  ClickTracker.wrappedComponent = WrappedComponent;
  ClickTracker.displayName = "ClickTracker(".concat(getDisplayName(WrappedComponent), ")");
  return ClickTracker;
};
function getDisplayName(WrappedComponent) {
  return (WrappedComponent === null || WrappedComponent === void 0 ? void 0 : WrappedComponent.displayName) || (WrappedComponent === null || WrappedComponent === void 0 ? void 0 : WrappedComponent.name) || 'Component';
}
function AnalyticsPlugin(options) {
  return {
    name: 'bernie-plugin-analytics',
    onBrowserHydrateRenderContext: function (context, next) {
      var analyticsImplementation = new BernieAnalytics();
      var hydratedState = context.hydratedState,
        setAppData = context.setAppData,
        getAppData = context.getAppData;
      var clickstreamHydratedState = hydratedState === null || hydratedState === void 0 ? void 0 : hydratedState.clickstreamOptions;
      var logger = getAppData().logger;
      setAppData({
        analytics: analyticsImplementation,
        clickstreamAnalytics: getClickstreamAnalytics(__assign(__assign(__assign({}, options), clickstreamHydratedState), {
          logger: logger
        }), analyticsImplementation)
      });
      return next(context);
    },
    onBrowserWrapRootElement: function (context, next) {
      var getAppData = context.getAppData,
        setRootElement = context.setRootElement;
      var _a = getAppData(),
        analytics = _a.analytics,
        clickstreamAnalytics = _a.clickstreamAnalytics;
      setRootElement(function (RootElement) {
        return function (props) {
          return React.createElement(BernieAnalyticsProvider, {
            analytics: analytics
          }, React.createElement(ClickstreamAnalyticsProvider, {
            clickstreamAnalytics: clickstreamAnalytics
          }, React.createElement(RootElement, null, props.children)));
        };
      });
      return next(context);
    }
  };
}
export { withClickTracker as ClickTracker, AnalyticsPlugin as default, getPerformanceMetrics, useBernieAnalyticsContext };